/**
 * Cloud Service for Template Configuration Storage
 *
 * Supports two modes:
 * 1. Design Mode: Template + config + dummy data for styling/placement discovery
 * 2. Preview Mode: Template + config + real link data for specific page preview
 */

import { TemplateConfig, PlacementSelector, PreviewLinkData } from './types';

// Configuration - Uses Quattr Node.js backend API with npoint.io fallback for local dev
const isLocalDev = typeof window !== 'undefined' && window.location.hostname === 'localhost';

// Backend API URL (Node.js server) - e.g., https://api-dev.quattr.com
const backendApiUrl = process.env.NEXT_PUBLIC_QUATTR_API_URL || '';

const CLOUD_CONFIG = {
  provider: isLocalDev ? ('npoint' as const) : ('quattr' as const),
  // For local dev, use npoint.io since localhost isn't accessible from external sites
  // For staging/prod, use Node.js backend API via qwa-backend proxy
  backendApiUrl: backendApiUrl,
  documentId: 'c9da5064af796b9a1171', // Fallback npoint document ID for local dev
  // Quattr API endpoints (via qwa-backend proxy for auth)
  // Backend URL is like https://api-dev.quattr.com, routes mounted at /api/discoverability/...
  saveEndpoint: '/api/discoverability/template-config',
  // Public endpoint for bookmarklet (direct access to Node.js backend)
  // Public routes mounted at /public-api/...
  publicEndpoint: '/public-api/template-config',
};

// Local storage key for module-specific configs
const LOCAL_STORAGE_KEY_PREFIX = 'quattr_template_config_';

export interface CloudConfig {
  // Module identification (for multi-user support)
  moduleId?: string;
  moduleName?: string;

  // Template configuration
  templateId: string;
  templateName: string;
  config: TemplateConfig;

  // Mode-specific data
  mode: 'design' | 'preview';

  // For preview mode - actual links to display
  links?: PreviewLinkData[];
  pageUrl?: string;
  pageTitle?: string;

  // Placement info
  placement?: PlacementSelector;

  // Metadata
  timestamp: number;
  savedBy?: string; // User identifier for debugging

  // Legacy field for backwards compatibility
  html?: string;
}

export interface CloudServiceResult {
  success: boolean;
  error?: string;
  data?: CloudConfig;
  warning?: string; // Warning about overwriting another module's config
  previousModule?: { moduleId?: string; moduleName?: string }; // Previous config info
}

/**
 * Save to localStorage (module-specific, persists across sessions)
 */
function saveToLocalStorage(config: CloudConfig): void {
  if (typeof window === 'undefined') return;

  try {
    // Save module-specific config
    if (config.moduleId) {
      const key = `${LOCAL_STORAGE_KEY_PREFIX}${config.moduleId}`;
      localStorage.setItem(key, JSON.stringify(config));
    }
    // Also save as "current active" config for quick reference
    localStorage.setItem(`${LOCAL_STORAGE_KEY_PREFIX}current`, JSON.stringify(config));
  } catch (e) {
    console.warn('Failed to save to localStorage:', e);
  }
}

/**
 * Load from localStorage
 */
function loadFromLocalStorage(moduleId?: string): CloudConfig | null {
  if (typeof window === 'undefined') return null;

  try {
    const key = moduleId ? `${LOCAL_STORAGE_KEY_PREFIX}${moduleId}` : `${LOCAL_STORAGE_KEY_PREFIX}current`;
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    console.warn('Failed to load from localStorage:', e);
    return null;
  }
}

/**
 * Check what's currently in cloud storage (to warn about overwrites)
 */
async function checkCloudConfig(moduleId?: string): Promise<CloudConfig | null> {
  try {
    let url: string;
    const options: RequestInit = {};

    if (CLOUD_CONFIG.provider === 'npoint') {
      url = `https://api.npoint.io/${CLOUD_CONFIG.documentId}?t=${Date.now()}`;
    } else {
      if (!moduleId) {
        return null;
      }
      // Use qwa-backend proxy for authenticated requests
      const backendUrl = encodeURIComponent(
        `${CLOUD_CONFIG.backendApiUrl}${CLOUD_CONFIG.saveEndpoint}?moduleId=${encodeURIComponent(moduleId)}`
      );
      url = `/api/qwa-backend?url=${backendUrl}&t=${Date.now()}`;
      options.credentials = 'include';
    }

    const response = await fetch(url, options);
    if (!response.ok) return null;
    const data = await response.json();
    return data?.templateId ? data : null;
  } catch {
    return null;
  }
}

/**
 * Save template configuration to cloud storage
 * Also saves to localStorage for module-specific persistence
 */
export async function saveConfigToCloud(config: CloudConfig): Promise<CloudServiceResult> {
  try {
    // First, check what's currently in cloud to warn about overwrites
    const existingConfig = await checkCloudConfig(config.moduleId);
    let warning: string | undefined;
    let previousModule: { moduleId?: string; moduleName?: string } | undefined;

    if (existingConfig && existingConfig.moduleId && existingConfig.moduleId !== config.moduleId) {
      warning = `Note: This will overwrite config for module "${existingConfig.moduleName || existingConfig.moduleId}"`;
      previousModule = { moduleId: existingConfig.moduleId, moduleName: existingConfig.moduleName };
    }

    // Save to cloud (shared storage - will be visible in bookmarklet)
    let url: string;
    const options: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(config),
    };

    if (CLOUD_CONFIG.provider === 'npoint') {
      url = `https://api.npoint.io/${CLOUD_CONFIG.documentId}`;
    } else {
      if (!config.moduleId) {
        throw new Error('moduleId is required to save template configuration');
      }
      // Use qwa-backend proxy for authenticated requests
      const backendUrl = encodeURIComponent(
        `${CLOUD_CONFIG.backendApiUrl}${CLOUD_CONFIG.saveEndpoint}?moduleId=${encodeURIComponent(config.moduleId)}`
      );
      url = `/api/qwa-backend?url=${backendUrl}`;
      options.credentials = 'include';
    }

    const response = await fetch(url, options);

    if (!response.ok) {
      throw new Error(`Server returned ${response.status}`);
    }

    // Also save to localStorage (module-specific persistence)
    saveToLocalStorage(config);

    return { success: true, data: config, warning, previousModule };
  } catch (error) {
    console.error('Failed to save config to cloud:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

/**
 * Save for design mode - with sample data for styling
 */
export async function saveDesignModeConfig(
  templateId: string,
  templateName: string,
  config: TemplateConfig,
  placement?: PlacementSelector
): Promise<CloudServiceResult> {
  return saveConfigToCloud({
    templateId,
    templateName,
    config,
    mode: 'design',
    placement,
    timestamp: Date.now(),
  });
}

/**
 * Save for preview mode - with actual link data
 */
export async function savePreviewModeConfig(
  templateId: string,
  templateName: string,
  config: TemplateConfig,
  links: PreviewLinkData[],
  pageUrl: string,
  pageTitle: string,
  placement?: PlacementSelector,
  moduleId?: string,
  moduleName?: string
): Promise<CloudServiceResult> {
  return saveConfigToCloud({
    moduleId,
    moduleName,
    templateId,
    templateName,
    config,
    mode: 'preview',
    links,
    pageUrl,
    pageTitle,
    placement,
    timestamp: Date.now(),
  });
}

/**
 * Load template configuration from cloud storage
 */
export async function loadConfigFromCloud(moduleId?: string): Promise<CloudServiceResult> {
  try {
    let url: string;
    const options: RequestInit = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    };

    if (CLOUD_CONFIG.provider === 'npoint') {
      url = `https://api.npoint.io/${CLOUD_CONFIG.documentId}?t=${Date.now()}`;
    } else {
      if (!moduleId) {
        throw new Error('moduleId is required to load template configuration');
      }
      // Use qwa-backend proxy for authenticated requests
      const backendUrl = encodeURIComponent(
        `${CLOUD_CONFIG.backendApiUrl}${CLOUD_CONFIG.saveEndpoint}?moduleId=${encodeURIComponent(moduleId)}`
      );
      url = `/api/qwa-backend?url=${backendUrl}&t=${Date.now()}`;
      options.credentials = 'include';
    }

    const response = await fetch(url, options);

    if (!response.ok) {
      throw new Error(`Server returned ${response.status}`);
    }

    const data = await response.json();

    if (!data || !data.templateId) {
      return {
        success: false,
        error: 'No configuration found. Please configure a module first.',
      };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Failed to load config from cloud:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to load configuration',
    };
  }
}

/**
 * Generate the bookmarklet code
 * Supports both design mode (extract links from page) and preview mode (use saved links)
 * Updated: Now supports all 11 template types
 *
 * The template literal below IS the shipped script, so keep it comment-free:
 * anything written inside it is encoded into the `javascript:` URL that users
 * carry in their bookmarks bar. Notes that would otherwise live inline:
 *
 * 1. Quoting. Never write an inline `onclick="...'...'..."` handler in here. A
 *    template literal collapses `\'` to a bare `'`, which closes the
 *    surrounding single-quoted JS string and makes the WHOLE bookmarklet fail
 *    to parse - so nothing runs, on any site. That shipped twice (2026-02-03,
 *    fixed by 5103c7a3b; and 2026-03-27 via the Image Carousel). Attach
 *    handlers in code instead, as setupCarouselHandlers does. Inline handlers
 *    are also blocked by Content-Security-Policy on customer sites.
 *
 * 2. `<script>` tags are useless here. The module is inserted with innerHTML,
 *    which never executes script elements.
 *
 * 3. The CSS reset in renderTemplate is scoped so it cannot outrank a
 *    template. `:where(.quattr-module)` adds zero specificity, and the element
 *    layer `:where(.quattr-module) :is(div,p,a,...)` reaches only 0,0,1 - just
 *    enough to beat a host rule like `p{line-height:2.4}` on tie (our style is
 *    injected after the host's), while every template rule is 0,1,0 or an
 *    inline style and still wins. text-align is set on the module root ONLY,
 *    because templates centre containers such as .q-ic-card and rely on their
 *    children inheriting that.
 *
 * 4. Only one placement session may be live. A bookmark click is not a page
 *    click, so it is not swallowed by an open session's click handler and the
 *    script can be re-entered. `window.__quattrPlacementCleanup` lets a new run
 *    tear the previous one down; without it two toolbars share one element id,
 *    `getElementById` returns the first, and the Cancel button the user can
 *    actually see ends up with no handler at all.
 *
 * @param schema - Customer schema (e.g., 'quattr', 'customer_name')
 * @param moduleId - Module ID for the internal linking module
 */
export function generateBookmarkletCode(schema?: string, moduleId?: string): string {
  const apiUrl = getBookmarkletApiUrl(schema, moduleId);

  const code = `(function(){
var API_URL="${apiUrl}?t="+Date.now();
fetch(API_URL).then(function(r){return r.json();}).then(function(data){
if(!data||!data.templateId){alert("No module configured. Please set up a module in Quattr first.");return;}

// Show module info toast so user knows which module's config they're previewing
var moduleInfo=escapeHtml(data.moduleName||data.moduleId||"Unknown");
var templateName=escapeHtml(data.templateName||"Unknown");
var infoToast=document.createElement("div");
infoToast.style.cssText="position:fixed;top:60px;right:20px;background:#1e3a5f;color:white;padding:12px 20px;border-radius:8px;font-family:system-ui,sans-serif;font-size:13px;z-index:100002;box-shadow:0 4px 20px rgba(0,0,0,0.3);max-width:300px;";
infoToast.innerHTML="<div style='font-weight:600;margin-bottom:4px;'>&#128279; Quattr Preview</div><div style='opacity:0.9;'>Module: "+moduleInfo+"</div><div style='opacity:0.7;font-size:11px;margin-top:4px;'>Template: "+templateName+"</div>";
document.body.appendChild(infoToast);
setTimeout(function(){infoToast.style.opacity="0";infoToast.style.transition="opacity 0.3s";setTimeout(function(){infoToast.remove();},300);},5000);

var links;
var autoPlace=false;
var placementSelector=data.placement?data.placement.selector:null;
var placementPosition=data.placement?data.placement.position:"after";

if(data.mode=="preview"&&data.links&&data.links.length>0){
links=data.links;
autoPlace=!!placementSelector;
}else{
links=extractLinks();
if(links.length==0){alert("No internal links found on this page.");return;}
}

var renderedHtml=renderTemplate(data.templateId,data.config,links);

if(autoPlace){
var targetEl=document.querySelector(placementSelector);
if(targetEl){
autoInsertModule(targetEl,renderedHtml,placementPosition);
return;
}
}
startPlacement(renderedHtml,placementSelector);
}).catch(function(e){alert("Failed to load config: "+e.message);});

function extractLinks(){
var currentHost=window.location.hostname;
var links=[];
var seen=new Set();
var allLinks=document.querySelectorAll("a[href]");
for(var i=0;i<allLinks.length&&links.length<20;i++){
var a=allLinks[i];
try{
var url=new URL(a.href,window.location.origin);
if(url.hostname==currentHost&&!seen.has(url.pathname)&&url.pathname!=window.location.pathname&&url.pathname.length>1){
seen.add(url.pathname);
var text=a.textContent.trim()||a.title||url.pathname;
if(text.length>3&&text.length<100){
links.push({url:a.href,anchorText:text,title:a.title||text});
}}}catch(e){}}
return links;
}

function renderTemplate(tid,c,links){
var e=escapeHtml;
var max=c.maxLinks||c.maxCards||8;
var lnks=links.slice(0,max);
var base='font-family:system-ui,-apple-system,sans-serif;';
var cls=c.customCssClass?' '+c.customCssClass:'';
var styles='';
var html='';

switch(tid){
case 'vertical-link-list':{
var hd=c.moduleHeading||'Related Articles';
var ht=c.headingTag||'h3';
var ls=c.listStyle||'none';
var vm=c.showViewMore!=false;
var vt=c.viewMoreText||'View More';
var sp=c.itemSpacing=='tight'?'4px':c.itemSpacing=='relaxed'?'12px':'8px';
var cpd=c.containerPadding=='none'?'0':c.containerPadding=='sm'?'0.5rem':c.containerPadding=='lg'?'1.5rem':'1rem';
styles='<style>.q-vll a{color:#2563eb;text-decoration:none;}.q-vll a:hover{text-decoration:underline;}.q-vll li{margin:'+sp+' 0;}.q-vll .q-hidden{display:none;}</style>';
var lst=ls=='none'?'list-style:none;padding-left:0;':ls=='bullets'?'list-style:disc;padding-left:20px;':'list-style:decimal;padding-left:20px;';
html='<div class="quattr-module q-vll'+cls+'" data-max="'+max+'" data-vt="'+e(vt)+'" style="'+base+'padding:'+cpd+';"><'+ht+' style="color:#1e3a5f;margin:0 0 12px;">'+e(hd)+'</'+ht+'><ul style="'+lst+'margin:0;">'+lnks.map(function(l,i){return '<li class="q-vll-item"><a href="'+l.url+'">'+e(l.anchorText)+'</a></li>';}).join('')+'</ul>'+(vm&&links.length>max?'<a href="#" class="q-view-more" data-expanded="false" style="display:inline-block;margin-top:12px;color:#3ecf8e;">'+e(vt)+' &rarr;</a>':'')+'</div>';
break;}

case 'multi-column-links':{
var sh=c.sectionHeading||'';
var cols=parseInt(c.columns)||3;
var mpl=c.maxLinksPerModule||5;
var vm=c.showViewMore!=false;
var cd=c.columnDivider!=false;
var bg=c.containerBackground||'#f9fafb';
var hdgs=['Related Products','Related Blogs','Related Tags','More Links','Featured'];
styles='<style>.q-mcl{display:flex;gap:24px;flex-wrap:wrap;}.q-mcl>div{flex:1;min-width:150px;'+(cd?'border-right:1px solid #e5e7eb;padding-right:24px;':'')+'}.q-mcl>div:last-child{border-right:none;padding-right:0;}.q-mcl h6{color:#1e3a5f;margin:0 0 8px;font-size:14px;}.q-mcl a{color:#2563eb;text-decoration:none;display:block;padding:4px 0;font-size:13px;}.q-mcl a:hover{text-decoration:underline;}.q-mcl .q-hidden{display:none;}</style>';
var linksPerCol=Math.ceil(links.length/cols);
var chunks=[];for(var i=0;i<cols;i++){chunks.push(links.slice(i*linksPerCol,(i+1)*linksPerCol));}
html='<div class="quattr-module q-mcl'+cls+'" data-mpl="'+mpl+'" style="'+base+'padding:16px;background:'+bg+';border-radius:8px;">'+(sh?'<h6 style="color:#1e3a5f;margin:0 0 16px;font-size:16px;font-weight:600;">'+e(sh)+'</h6>':'')+chunks.map(function(ch,ci){var colHasMore=ch.length>mpl;return '<div data-col="'+ci+'"><h6>'+hdgs[ci%hdgs.length]+'</h6><div>'+ch.map(function(l,li){return '<a href="'+l.url+'" class="q-mcl-link'+(li<mpl?'':' q-hidden')+'">'+e(l.anchorText)+'</a>';}).join('')+(vm&&colHasMore?'<a href="#" class="q-col-more" data-col="'+ci+'" data-expanded="false" style="color:#64748b;margin-top:8px;">View More</a>':'')+'</div></div>';}).join('')+'</div>';
break;}

case 'image-card-grid':{
var hd=c.moduleHeading||'Related Products';
var ht=c.headingTag||'h2';
var gc=c.gridColumns=='auto-fit'?3:parseInt(c.gridColumns)||3;
var csz=c.cardSize||'medium';
var cst=c.cardStyle||'default';
var iar=c.imageAspectRatio||'1:1';
var st=c.showTitle!=false;
var sd=c.showDescription||false;
var tp=c.titlePosition||'below';
var cbr=c.cardBorderRadius=='none'?'0':c.cardBorderRadius=='large'?'16px':'8px';
var he=c.hoverEffect||'lift';
var imgH=csz=='small'?'80px':csz=='large'?'180px':'120px';
var fs=csz=='small'?'12px':csz=='large'?'16px':'14px';
var hoverCss=he=='lift'?'transform:translateY(-4px);box-shadow:0 8px 24px rgba(0,0,0,0.15);':he=='zoom'?'transform:scale(1.02);':he=='shadow'?'box-shadow:0 8px 24px rgba(0,0,0,0.15);':'opacity:0.9;';
styles='<style>.q-icg{display:grid;grid-template-columns:repeat('+gc+',1fr);gap:16px;}.q-icg-card{background:#fff;border-radius:'+cbr+';overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.1);transition:all 0.2s;}.q-icg-card:hover{'+hoverCss+'}.q-icg-img{height:'+imgH+';object-fit:cover;width:100%;}.q-icg-title{padding:12px;color:#1e3a5f;font-weight:500;font-size:'+fs+';text-decoration:none;display:block;}.q-icg-title:hover{color:#3ecf8e;}.q-icg-desc{padding:0 12px 12px;color:#64748b;font-size:12px;margin:0;}</style>';
var grads=['#3ecf8e,#14b8a6','#f59e0b,#ef4444','#8b5cf6,#ec4899','#06b6d4,#3b82f6'];
html='<div class="quattr-module'+cls+'" style="'+base+'"><'+ht+' style="color:#1e3a5f;margin:0 0 16px;">'+e(hd)+'</'+ht+'><div class="q-icg">'+lnks.map(function(l,i){var img=l.image?'<img class="q-icg-img" src="'+l.image+'" alt="'+e(l.anchorText)+'">':'<div class="q-icg-img" style="background:linear-gradient(135deg,'+grads[i%grads.length]+');"></div>';return '<div class="q-icg-card">'+img+(st?'<a href="'+l.url+'" class="q-icg-title">'+e(l.anchorText)+'</a>':'')+(sd?'<p class="q-icg-desc">'+e(l.description||l.anchorText)+'</p>':'')+'</div>';}).join('')+'</div></div>';
break;}

case 'rich-article-cards':{
var hd=c.moduleHeading||'You Might Also Like';
var ly=c.layout||'grid';
var gc=parseInt(c.gridColumns)||3;
var csz=c.cardSize||'medium';
var si=c.showImage!=false;
var iar=c.imageAspectRatio||'16:9';
var sc=c.showCategory||false;
var sdt=c.showDate||false;
var srt=c.showReadingTime||false;
var se=c.showExcerpt||false;
var imgH=csz=='small'?'100px':csz=='large'?'180px':'140px';
var cats=['MARKETING','SEO','TECH','NEWS','GUIDE'];
var layoutCss=ly=='list'?'display:flex;flex-direction:column;gap:16px;':'display:grid;grid-template-columns:repeat('+gc+',1fr);gap:20px;';
styles='<style>.q-rac{'+layoutCss+'}.q-rac-card{background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.08);'+(ly=='list'?'display:flex;':'')+'}.q-rac-img{height:'+imgH+';background:linear-gradient(135deg,#667eea,#764ba2);'+(ly=='list'?'width:200px;flex-shrink:0;':'')+'object-fit:cover;width:'+(ly=='list'?'200px':'100%')+';}.q-rac-body{padding:16px;flex:1;}.q-rac-cat{font-size:11px;color:#3ecf8e;font-weight:600;margin-bottom:8px;}.q-rac-title{color:#1e3a5f;font-weight:600;font-size:15px;margin-bottom:8px;text-decoration:none;display:block;}.q-rac-excerpt{color:#64748b;font-size:13px;margin:0 0 8px;line-height:1.5;}.q-rac-meta{font-size:12px;color:#94a3b8;}</style>';
var grads=['#667eea,#764ba2','#f093fb,#f5576c','#4facfe,#00f2fe','#43e97b,#38f9d7'];
html='<div class="quattr-module'+cls+'" style="'+base+'"><h6 style="color:#1e3a5f;margin:0 0 16px;font-size:18px;">'+e(hd)+'</h6><div class="q-rac">'+lnks.map(function(l,i){var img=l.image?'<img class="q-rac-img" src="'+l.image+'" alt="'+e(l.anchorText)+'">':'<div class="q-rac-img" style="background:linear-gradient(135deg,'+grads[i%grads.length]+');"></div>';var meta=[];if(sdt)meta.push('Jan '+(10+i));if(srt)meta.push((5+i%4)+' min read');return '<div class="q-rac-card">'+(si?img:'')+'<div class="q-rac-body">'+(sc?'<div class="q-rac-cat">'+cats[i%cats.length]+'</div>':'')+'<a href="'+l.url+'" class="q-rac-title">'+e(l.anchorText)+'</a>'+(se?'<p class="q-rac-excerpt">'+e(l.description||l.anchorText)+'</p>':'')+(meta.length?'<div class="q-rac-meta">'+meta.join(' &middot; ')+'</div>':'')+'</div></div>';}).join('')+'</div></div>';
break;}

case 'image-carousel':{
var hd=c.moduleHeading||'Featured Products';
var vis=parseInt(c.visibleSlides)||4;
var csz=c.cardSize||'medium';
var cst=c.cardStyle||'image-title';
var iar=c.imageAspectRatio||'1:1';
var gp=c.gap=='none'?'0':c.gap=='sm'?'8px':c.gap=='lg'?'20px':'12px';
var sw=csz=='small'?'140px':csz=='large'?'220px':'180px';
var ih=iar=='16:9'?(csz=='small'?'79px':csz=='large'?'124px':'101px'):iar=='4:3'?(csz=='small'?'105px':csz=='large'?'165px':'135px'):iar=='portrait'?(csz=='small'?'187px':csz=='large'?'293px':'240px'):(csz=='small'?'140px':csz=='large'?'220px':'180px');
var fs=csz=='small'?'12px':csz=='large'?'15px':'13px';
var arw=c.showArrows!=false;
var dots=!!c.showDots;
var ap=!!c.autoPlay;
styles='<style>.q-ic{display:flex;gap:'+gp+';overflow-x:auto;padding-bottom:8px;scroll-snap-type:x mandatory;scroll-behavior:smooth;}.q-ic::-webkit-scrollbar{height:6px;}.q-ic::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:3px;}.q-ic-card{flex:0 0 '+sw+';scroll-snap-align:start;text-align:center;}.q-ic-img{width:100%;height:'+ih+';border-radius:8px;object-fit:cover;}.q-ic-title{color:#1e3a5f;font-size:'+fs+';text-decoration:none;display:block;margin-top:8px;}.q-ic-title:hover{color:#3ecf8e;}.q-ic-nav{border:1px solid #e2e8f0;border-radius:4px;padding:4px 8px;background:#fff;cursor:pointer;font-size:14px;}.q-ic-dots{display:flex;justify-content:center;gap:6px;margin-top:12px;}.q-ic-dot{width:8px;height:8px;border-radius:50%;background:#ddd;}.q-ic-dot.active{background:#2db77a;}</style>';
var grads=['#3ecf8e,#14b8a6','#f59e0b,#ef4444','#8b5cf6,#ec4899','#06b6d4,#3b82f6','#10b981,#059669'];
var cards=lnks.map(function(l,i){var img=l.image?'<img class="q-ic-img" src="'+l.image+'" alt="'+e(l.anchorText)+'">':'<div class="q-ic-img" style="background:linear-gradient(135deg,'+grads[i%grads.length]+');"></div>';return '<div class="q-ic-card">'+img+(cst!='image-only'?'<a href="'+l.url+'" class="q-ic-title">'+e(l.anchorText)+'</a>':'')+'</div>';}).join('');
html='<div class="quattr-module'+cls+'" style="'+base+'"><div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;"><h6 style="color:#1e3a5f;margin:0;font-size:1.1rem;font-weight:600;">'+e(hd)+'</h6>'+(arw?'<div style="display:flex;gap:4px;"><button class="q-ic-nav" data-q-nav="prev" data-q-track="q-ic" data-q-step="'+parseInt(sw)+'">&#8249;</button><button class="q-ic-nav" data-q-nav="next" data-q-track="q-ic" data-q-step="'+parseInt(sw)+'">&#8250;</button></div>':'')+'</div><div class="q-ic"'+(ap?' data-q-autoplay="'+parseInt(sw)+'"':'')+'>'+cards+'</div>'+(dots?'<div class="q-ic-dots">'+[0,1,2].map(function(i){return '<span class="q-ic-dot'+(i==0?' active':'')+'"></span>';}).join('')+'</div>':'')+'</div>';
break;}

case 'sidebar-link-box':{
var hd=c.moduleHeading||'Trending Posts';
var hs=c.headingStyle||'uppercase';
var ml=parseInt(c.maxLinks)||max;
var lks=c.linkStyle||'plain';
var sd=c.showDividers!=false;
var expandable=c.expandable||false;
var initVis=c.initialVisible||5;
var csBg=c.containerStyle=='card'?'background:#fff;box-shadow:0 2px 8px rgba(0,0,0,0.08);':'background:#fff;';
var slbLinks=links.slice(0,ml);
var hasMore=expandable&&slbLinks.length>initVis;
styles='<style>.q-slb{'+csBg+'border:1px solid #e5e7eb;border-radius:8px;padding:16px;max-width:280px;}.q-slb h6{color:#1e3a5f;margin:0 0 12px;font-size:14px;'+(hs=='uppercase'?'text-transform:uppercase;letter-spacing:0.5px;':hs=='underlined'?'border-bottom:2px solid #3ecf8e;padding-bottom:8px;':'font-weight:700;')+'}.q-slb a{color:#374151;text-decoration:none;display:block;padding:8px 0;font-size:13px;'+(sd?'border-bottom:1px solid #f3f4f6;':'')+'}.q-slb a:hover{color:#3ecf8e;}.q-slb a:last-child{border-bottom:none;}.q-slb .q-hidden{display:none;}</style>';
html='<div class="quattr-module q-slb'+cls+'" data-init="'+initVis+'" style="'+base+'"><h6>'+e(hd)+'</h6><div>'+slbLinks.map(function(l,i){return '<a href="'+l.url+'" class="q-slb-link'+(hasMore&&!(i<initVis)?' q-hidden':'')+'">'+e(l.anchorText)+'</a>';}).join('')+(hasMore?'<a href="#" class="q-view-more" data-expanded="false" style="color:#64748b;margin-top:8px;display:block;">Show More</a>':'')+'</div></div>';
break;}

case 'sidebar-card-stack':{
var hd=c.moduleHeading||'Relevant Blogs';
var ml=parseInt(c.maxCards)||max;
var si=c.showImage!=false;
var isz=c.imageSize||'medium';
var ip=c.imagePosition||'left';
var sc=c.showCategory||false;
var cbg=c.cardBackground||'#fff';
var imgW=isz=='small'?'60px':isz=='large'?'100px':'80px';
var imgH=isz=='small'?'45px':isz=='large'?'75px':'60px';
var cats=['MARKETING','SEO','TECH','NEWS','GUIDE'];
styles='<style>.q-scs{max-width:300px;}.q-scs h6{color:#1e3a5f;margin:0 0 12px;font-size:16px;}.q-scs-card{display:flex;'+(ip=='right'?'flex-direction:row-reverse;':'')+'gap:12px;padding:12px 0;border-bottom:1px solid #f3f4f6;background:'+cbg+';}.q-scs-card:last-child{border-bottom:none;}.q-scs-img{width:'+imgW+';height:'+imgH+';border-radius:6px;flex-shrink:0;object-fit:cover;}.q-scs-title{color:#374151;font-size:13px;font-weight:500;text-decoration:none;line-height:1.4;}.q-scs-title:hover{color:#3ecf8e;}.q-scs-cat{font-size:10px;color:#3ecf8e;font-weight:600;margin-bottom:4px;}</style>';
var grads=['#3ecf8e,#14b8a6','#f59e0b,#ef4444','#8b5cf6,#ec4899','#06b6d4,#3b82f6'];
html='<div class="quattr-module q-scs'+cls+'" style="'+base+'"><h6>'+e(hd)+'</h6><div>'+links.slice(0,ml).map(function(l,i){var img=l.image?'<img class="q-scs-img" src="'+l.image+'" alt="'+e(l.anchorText)+'">':'<div class="q-scs-img" style="background:linear-gradient(135deg,'+grads[i%grads.length]+');"></div>';return '<div class="q-scs-card">'+(si?img:'')+'<div>'+(sc?'<div class="q-scs-cat">'+cats[i%cats.length]+'</div>':'')+'<a href="'+l.url+'" class="q-scs-title">'+e(l.anchorText)+'</a></div></div>';}).join('')+'</div></div>';
break;}

case 'inline-reading-callout':{
var hd=c.calloutHeading||'Related Reading';
var st=c.style||'left-border';
var si=c.showIcon!=false;
var ml=parseInt(c.maxLinks)||3;
var ld=c.linkDisplay||'list';
var fp=c.floatPosition||'none';
var wd=c.width||'100%';
var bdr=st=='left-border'?'border-left:4px solid #3ecf8e;padding-left:16px;':st=='bordered'?'border:1px solid #e5e7eb;border-radius:8px;padding:16px;':'background:#f8fafc;border-radius:8px;padding:16px;';
var floatCss=fp=='left'?'float:left;margin-right:20px;width:'+wd+';':fp=='right'?'float:right;margin-left:20px;width:'+wd+';':'width:'+wd+';';
var linkCss=ld=='inline'?'display:inline;padding:0;':'display:block;padding:4px 0;';
styles='<style>.q-irc a{color:#2563eb;text-decoration:none;'+linkCss+'}.q-irc a:hover{text-decoration:underline;}'+(ld=='inline'?'.q-irc a+a:before{content:" • ";color:#94a3b8;}':'')+'</style>';
html='<div class="quattr-module q-irc'+cls+'" style="'+base+bdr+'margin:20px 0;'+floatCss+'"><h6 style="color:#1e3a5f;margin:0 0 8px;font-size:15px;">'+(si?'&#128218; ':'')+e(hd)+'</h6><div>'+lnks.slice(0,ml).map(function(l){return '<a href="'+l.url+'">'+e(l.anchorText)+'</a>';}).join('')+'</div></div>';
break;}

case 'contextual-link-box':{
var hsrc=c.headingSource||'anchorText';
var hd=c.heading||'Dive Deeper';
var si=!!c.showIcon;
var simg=c.showImage!=false;
var ip=c.imagePosition||'left';
var iw=c.imageWidth||'200px';
var ih=c.imageHeight||'150px';
var sd=c.showDescription!=false;
var sa=!!c.showAuthor;
var srt=!!c.showReadTime;
var spd=!!c.showPublishDate;
var ct=c.ctaText||'Read More';
var cs=c.ctaStyle||'button';
var csh=c.ctaShape||'pill';
var cc=c.ctaColor||'#2db77a';
var csi=c.ctaShowIcon!=false;
var cp=c.ctaPosition||'right';
var hvc=c.hoverColor||'#1e293b';
var bs=c.borderStyle||'solid';
var brd=bs=='solid'?'border:1px solid #e2e8f0;':bs=='dashed'?'border:2px dashed #e2e8f0;':'';
var mb=parseInt(c.maxBoxes)||6;
var exp=!!c.expandable;
var initVis=parseInt(c.initialVisible)||3;
var smc=c.showMoreColor||'#008fbf';
var vmt=c.viewMoreText||'Show More';
var allBoxLnks=lnks.slice(0,mb);
var hasMore=exp&&allBoxLnks.length>initVis;
var moreCount=allBoxLnks.length-initVis;
var isTop=ip=='top';
var ctaRad=csh=='pill'?'50px':csh=='rounded'?'8px':'0';
var ctaBtnCss=cs=='button'?'background:'+cc+';color:#fff;padding:10px 24px;border-radius:'+ctaRad+';display:inline-block;':'color:'+cc+';';
var ctaIcon=csi?' &rarr;':'';
var isGrid=cp=='right'&&!isTop&&simg;
var gridCols=isGrid?'display:grid;grid-template-columns:'+iw+' 1fr auto;':'display:flex;'+(ip=='right'?'flex-direction:row-reverse;':isTop?'flex-direction:column;':'');
styles='<style>.q-clb-wrap{display:flex;flex-direction:column;gap:12px;font-family:system-ui,-apple-system,sans-serif;}.q-clb{'+gridCols+'gap:20px;align-items:'+(isTop?'stretch':'center')+';background:#f8fafc;border-radius:12px;padding:20px;'+brd+'transition:all 0.3s ease;}.q-clb:hover{background:'+hvc+';box-shadow:0 4px 20px rgba(0,0,0,0.15);}.q-clb:hover .q-clb-heading,.q-clb:hover .q-clb-meta{color:#fff;}.q-clb:hover .q-clb-desc{color:#cbd5e1;}.q-clb.q-hidden{display:none;}.q-clb-img{width:100%;height:'+ih+';border-radius:8px;overflow:hidden;background:#e2e8f0;}.q-clb-img img{width:100%;height:100%;object-fit:cover;}.q-clb-content{min-width:0;}.q-clb-heading{color:#1e3a5f;margin:0 0 6px;font-size:16px;font-weight:600;line-height:1.4;}.q-clb-meta{display:flex;flex-wrap:wrap;align-items:center;gap:6px;color:#94a3b8;font-size:13px;margin-bottom:8px;}.q-clb-desc{color:#64748b;font-size:14px;margin:0;line-height:1.6;}.q-clb-cta-wrap{display:flex;align-items:center;flex-shrink:0;}.q-clb-cta{'+ctaBtnCss+'text-decoration:none;font-weight:500;white-space:nowrap;}.q-clb-cta:hover{filter:brightness(1.1);text-decoration:none;}.q-clb-more{display:block;text-align:center;margin-top:8px;color:'+smc+';text-decoration:none;font-weight:500;cursor:pointer;font-size:15px;}@media(max-width:600px){.q-clb{display:flex!important;flex-direction:column!important;grid-template-columns:unset!important;}.q-clb-img{width:100%!important;height:160px;}}</style>';
html='<div class="quattr-module q-clb-wrap'+cls+'" data-init="'+initVis+'" style="'+base+'">'+allBoxLnks.map(function(link,i){var hiddenCls=hasMore&&i>=initVis?' q-hidden':'';var linkHd=hsrc=='custom'?e(hd):hsrc=='title'?e(link.title||link.anchorText||hd):hsrc=='h1'?e(link.h1||link.title||link.anchorText||hd):e(link.anchorText||link.title||hd);var meta=[];if(spd&&link.publishDate)meta.push(e(link.publishDate));if(srt&&link.readingTime)meta.push('&#128336; '+e(link.readingTime));if(sa&&link.author)meta.push('&#128100; '+e(link.author));var metaHtml=meta.length?'<div class="q-clb-meta">'+meta.join(' &ndash; ')+'</div>':'';var descHtml=sd&&link.description?'<p class="q-clb-desc">'+e(link.description)+'</p>':'';var imgHtml=simg?'<div class="q-clb-img">'+(link.image?'<img src="'+link.image+'" alt="'+e(link.title||link.anchorText)+'">':'')+'</div>':'';var ctaHtml='<div class="q-clb-cta-wrap"><a href="'+link.url+'" class="q-clb-cta">'+e(ct)+ctaIcon+'</a></div>';if(isGrid){return '<div class="q-clb'+hiddenCls+'">'+imgHtml+'<div class="q-clb-content"><div class="q-clb-heading">'+(si?'&#128214; ':'')+linkHd+'</div>'+metaHtml+descHtml+'</div>'+ctaHtml+'</div>';}if(cp=='right'&&!isTop){return '<div class="q-clb'+hiddenCls+'">'+imgHtml+'<div class="q-clb-content"><div class="q-clb-heading">'+(si?'&#128214; ':'')+linkHd+'</div>'+metaHtml+descHtml+'</div>'+ctaHtml+'</div>';}return '<div class="q-clb'+hiddenCls+'">'+imgHtml+'<div class="q-clb-content"><div class="q-clb-heading">'+(si?'&#128214; ':'')+linkHd+'</div>'+metaHtml+descHtml+ctaHtml+'</div></div>';}).join('')+(hasMore?'<a href="#" class="q-clb-more" data-expanded="false">'+e(vmt)+' ('+moreCount+' more)</a>':'')+'</div>';
break;}

case 'related-searches':{
var sh=c.showHeading!=false;
var ht=c.headingText||'Related Searches';
var hs=c.headingStyle||'with-icon';
var ps=c.pillStyle||'outlined';
var pc=c.pillColor||'#2563eb';
var tc=c.textColor||'#fff';
var pvs=c.hoverStyle||'darken';
var ssi=!!c.showSearchIcon;
var ly=c.layout||'wrap';
var cols=parseInt(c.columns)||3;
var mp=parseInt(c.maxPills)||10;
var ppr=parseInt(c.pillsPerRow)||5;
var exp=!!c.expandable;
var vmt=c.viewMoreText||'Show More';
var psz=c.pillSize||'medium';
var br=c.borderRadius=='square'?'4px':c.borderRadius=='rounded'?'8px':'50px';
var ppd=psz=='small'?'6px 12px':psz=='large'?'12px 20px':'8px 16px';
var pfs=psz=='small'?'12px':psz=='large'?'15px':'13px';
var pbg=ps=='filled'?pc:'transparent';
var pclr=ps=='filled'?tc:pc;
var pbdr=ps=='ghost'?'none':'1.5px solid '+pc;
var hvrCss=pvs=='darken'?(ps=='filled'?'filter:brightness(0.85);':'background:'+pc+';color:'+tc+';'):pvs=='lighten'?(ps=='filled'?'filter:brightness(1.15);':'background:'+pc+'15;'):pvs=='invert'?(ps=='filled'?'background:transparent;color:'+pc+';border:1.5px solid '+pc+';':'background:'+pc+';color:'+tc+';border:none;'):'';
var layoutCss=ly=='scroll'?'display:flex;gap:8px;overflow-x:auto;':ly=='grid'?'display:grid;grid-template-columns:repeat('+cols+',1fr);gap:8px;':'display:flex;flex-wrap:wrap;gap:8px;';
var cpd=c.containerPadding=='custom'&&c.customPadding?c.customPadding:c.containerPadding=='none'?'0':c.containerPadding=='sm'?'0.5rem':c.containerPadding=='lg'?'1.5rem':'1rem';
var searchSvg='<svg style="width:14px;height:14px;flex-shrink:0;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>';
var allPillLnks=lnks.slice(0,mp);
var hasMorePills=exp&&allPillLnks.length>ppr;
var pillMoreCount=allPillLnks.length-ppr;
styles='<style>.q-rs-pills{'+layoutCss+'}.q-rs-pill{display:inline-flex;align-items:center;gap:6px;padding:'+ppd+';border-radius:'+br+';background:'+pbg+';color:'+pclr+';border:'+pbdr+';text-decoration:none;font-size:'+pfs+';font-weight:500;white-space:nowrap;transition:all 0.2s;}.q-rs-pill:hover{'+hvrCss+'text-decoration:none !important;}.q-rs-pill.q-hidden{display:none;}</style>';
html='<div class="quattr-module'+cls+'" data-ppr="'+ppr+'" data-vmt="'+e(vmt)+'" style="'+base+'background:'+(c.backgroundColor||'#f8fafc')+';padding:'+cpd+';border-radius:8px;">'+(sh||hasMorePills?'<div style="display:flex;align-items:center;gap:8px;margin-bottom:14px;">'+(sh&&hs=='with-icon'?'<svg style="width:18px;height:18px;color:#666;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>':'')+(sh?'<h4 style="margin:0;flex:1;font-size:'+(hs=='minimal'?'13px':'15px')+';font-weight:'+(hs=='minimal'?'500':'600')+';color:'+(hs=='minimal'?'#666':'#1a1a2e')+';'+(hs=='minimal'?'text-transform:uppercase;letter-spacing:0.5px;':'')+'">'+e(ht)+'</h4>':'')+(hasMorePills?'<a href="#" class="q-rs-more" data-expanded="false" style="font-size:13px;color:'+pc+';text-decoration:none;font-weight:500;cursor:pointer;">'+e(vmt)+' ('+pillMoreCount+' more)</a>':'')+'</div>':'')+'<div class="q-rs-pills">'+allPillLnks.map(function(l,i){var hiddenCls=hasMorePills&&i>=ppr?' q-hidden':'';return '<a href="'+l.url+'" class="q-rs-pill'+hiddenCls+'">'+(ssi?searchSvg:'')+'<span>'+e(l.anchorText)+'</span></a>';}).join('')+'</div></div>';
break;}

case 'related-videos-carousel':{
var sh=c.showHeading!=false;
var ht=c.headingText||'Related Videos';
var hs=c.headingStyle||'with-icon';
var cst=c.cardStyle||'default';
var csz=c.cardSize||'medium';
var vc=parseInt(c.visibleCards)||4;
var mc=parseInt(c.maxCards)||8;
var spb=c.showPlayButton!=false;
var pbs=c.playButtonStyle||'circle';
var pbc=c.playButtonColor||'rgba(255,0,0,0.9)';
var sdr=c.showDuration!=false;
var stt=c.showTitle!=false;
var tl=parseInt(c.titleLines)||2;
var sch=c.showChannel||false;
var svw=c.showViews||false;
var arw=c.showArrows||false;
var dots=!!c.showDots;
var ap=!!c.autoPlay;
var gp=c.gap=='none'?'0':c.gap=='sm'?'8px':c.gap=='lg'?'20px':'16px';
var cbr=c.borderRadius=='none'?'0':c.borderRadius=='large'?'16px':'8px';
var he=c.hoverEffect||'lift';
var bg=c.backgroundColor||'transparent';
var sw=csz=='small'?'160px':csz=='large'?'260px':'200px';
var th=csz=='small'?'90px':csz=='large'?'146px':'112px';
var hoverCss=he=='lift'?'transform:translateY(-4px);box-shadow:0 8px 20px rgba(0,0,0,0.15);':he=='zoom'?'transform:scale(1.03);':he=='glow'?'box-shadow:0 0 20px rgba(62,207,142,0.3);':'';
var pbHtml=spb?(pbs=='rounded-square'?'<div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:36px;height:28px;background:'+pbc+';border-radius:6px;display:flex;align-items:center;justify-content:center;"><div style="border:6px solid transparent;border-left:10px solid #fff;margin-left:3px;"></div></div>':'<div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:40px;height:40px;background:'+pbc+';border-radius:50%;display:flex;align-items:center;justify-content:center;"><div style="border:8px solid transparent;border-left:12px solid #fff;margin-left:4px;"></div></div>'):'';
var durs=['15:42','22:18','8:35','31:20','18:55','45:10'];
var views=['1.2M','856K','2.1M','445K','678K','923K'];
var channels=['TechChannel','CodeReview','DevTalks','Tutorial Hub','LearnMore'];
styles='<style>.q-rvc{display:flex;gap:'+gp+';overflow-x:auto;padding-bottom:8px;scroll-behavior:smooth;}.q-rvc::-webkit-scrollbar{height:6px;}.q-rvc::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:3px;}.q-rvc-card{flex:0 0 '+sw+';background:#fff;border-radius:'+cbr+';overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.1);transition:all 0.2s;}.q-rvc-card:hover{'+hoverCss+'}.q-rvc-thumb{position:relative;height:'+th+';background:linear-gradient(135deg,#1e293b,#334155);overflow:hidden;}.q-rvc-thumb img{width:100%;height:100%;object-fit:cover;}.q-rvc-dur{position:absolute;bottom:8px;right:8px;background:rgba(0,0,0,0.8);color:#fff;padding:2px 6px;border-radius:4px;font-size:11px;}.q-rvc-info{padding:12px;}.q-rvc-title{color:#1e3a5f;font-size:13px;font-weight:500;margin-bottom:4px;text-decoration:none;display:block;line-height:1.3;display:-webkit-box;-webkit-line-clamp:'+tl+';-webkit-box-orient:vertical;overflow:hidden;}.q-rvc-title:hover{color:#3ecf8e;}.q-rvc-meta{color:#94a3b8;font-size:11px;}.q-rvc-nav{border:1px solid #e2e8f0;border-radius:4px;padding:4px 8px;background:#fff;cursor:pointer;font-size:14px;}.q-rvc-dots{display:flex;justify-content:center;gap:6px;margin-top:12px;}.q-rvc-dot{width:8px;height:8px;border-radius:50%;background:#ddd;}.q-rvc-dot.active{background:#2db77a;}</style>';
html='<div class="quattr-module'+cls+'" style="'+base+'background:'+bg+';">'+(sh?'<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;"><h6 style="color:#1e3a5f;margin:0;display:flex;align-items:center;gap:8px;">'+(hs=='with-icon'?'<svg width="20" height="20" fill="currentColor"><path d="M4 4h16v12H4V4zm2 2v8h12V6H6zm4 2l5 2-5 2V8z"/></svg>':'')+''+e(ht)+'</h6>'+(arw?'<div style="display:flex;gap:4px;"><button class="q-rvc-nav" data-q-nav="prev" data-q-track="q-rvc" data-q-step="'+parseInt(sw)+'">&#8249;</button><button class="q-rvc-nav" data-q-nav="next" data-q-track="q-rvc" data-q-step="'+parseInt(sw)+'">&#8250;</button></div>':'')+'</div>':'')+'<div class="q-rvc"'+(ap?' data-q-autoplay="'+parseInt(sw)+'"':'')+'>'+links.slice(0,mc).map(function(l,i){var meta=[];if(sch)meta.push(channels[i%channels.length]);if(svw)meta.push(views[i%views.length]+' views');return '<div class="q-rvc-card"><div class="q-rvc-thumb">'+(l.image?'<img src="'+l.image+'" alt="'+e(l.anchorText)+'">':'')+pbHtml+(sdr?'<span class="q-rvc-dur">'+durs[i%durs.length]+'</span>':'')+'</div><div class="q-rvc-info">'+(stt?'<a href="'+l.url+'" class="q-rvc-title">'+e(l.anchorText)+'</a>':'')+(meta.length?'<div class="q-rvc-meta">'+meta.join(' &middot; ')+'</div>':'')+'</div></div>';}).join('')+'</div>'+(dots?'<div class="q-rvc-dots">'+[0,1,2].map(function(i){return '<span class="q-rvc-dot'+(i==0?' active':'')+'"></span>';}).join('')+'</div>':'')+'</div>';
break;}

default:
html='<div class="quattr-module" style="'+base+'padding:16px;border:1px solid #e5e7eb;border-radius:8px;"><p style="color:#64748b;">Template: '+tid+'</p><ul style="margin:8px 0;padding-left:20px;">'+lnks.map(function(l){return '<li><a href="'+l.url+'" style="color:#2563eb;">'+e(l.anchorText)+'</a></li>';}).join('')+'</ul></div>';
}

var ccss=c.customCss||'';
var ELS='div,p,span,a,ul,ol,li,h1,h2,h3,h4,h5,h6,button,small,strong,em,figure,blockquote,img';
var reset='<style>'
+':where(.quattr-module),:where(.quattr-module) :where(*){box-sizing:border-box;}'
+':where(.quattr-module) :is('+ELS+'){line-height:1.45;letter-spacing:normal;word-spacing:normal;text-transform:none;font-style:normal;text-indent:0;font-family:inherit;}'
+':where(.quattr-module) :is(h1,h2,h3,h4,h5,h6,p,ul,ol,li,figure,blockquote){margin:0;padding:0;float:none;}'
+':where(.quattr-module) :is(ul,ol){list-style:none;}'
+':where(.quattr-module) :is(a){text-decoration:none;background:none;}'
+':where(.quattr-module) :is(img){max-width:100%;height:auto;border:0;}'
+':where(.quattr-module){text-align:left;}'
+'</style>';
return reset+styles+(ccss?'<style>'+ccss+'</style>':'')+html;
}

function escapeHtml(str){
var div=document.createElement("div");
div.textContent=str;
return div.innerHTML;
}

function setupCarouselHandlers(container){
var navs=container.querySelectorAll("[data-q-nav]");
navs.forEach(function(btn){
btn.onclick=function(ev){
ev.preventDefault();
var mod=btn.closest(".quattr-module");
if(!mod)return;
var track=mod.querySelector("."+btn.getAttribute("data-q-track"));
if(!track)return;
var step=parseInt(btn.getAttribute("data-q-step"))||200;
track.scrollBy({left:btn.getAttribute("data-q-nav")=="prev"?-step:step,behavior:"smooth"});
};
});
var tracks=container.querySelectorAll("[data-q-autoplay]");
tracks.forEach(function(track){
var step=parseInt(track.getAttribute("data-q-autoplay"))||200;
var timer=setInterval(function(){
if(!document.body.contains(track)){clearInterval(timer);return;}
if(track.scrollLeft+track.clientWidth<track.scrollWidth){track.scrollBy({left:step,behavior:"smooth"});}
else{track.scrollLeft=0;}
},3000);
});
}

function setupViewMoreHandlers(container){
var btns=container.querySelectorAll(".q-view-more");
btns.forEach(function(btn){
btn.onclick=function(e){
e.preventDefault();
var mod=btn.closest(".quattr-module");
if(!mod)return;
var expanded=btn.getAttribute("data-expanded")=="true";
var maxVal=parseInt(mod.getAttribute("data-max"))||8;
var initVal=parseInt(mod.getAttribute("data-init"))||5;
var vt=mod.getAttribute("data-vt")||"View More";
var items=mod.querySelectorAll(".q-vll-item,.q-slb-link");
var limit=mod.classList.contains("q-slb")?initVal:maxVal;
items.forEach(function(item,i){
if(!(i<limit))item.classList.toggle("q-hidden",expanded);
});
btn.setAttribute("data-expanded",expanded?"false":"true");
if(mod.classList.contains("q-vll")){
btn.innerHTML=expanded?vt+" &rarr;":"&larr; Show Less";
}else{
btn.textContent=expanded?"Show More":"Show Less";
}
};
});
var colBtns=container.querySelectorAll(".q-col-more");
colBtns.forEach(function(btn){
btn.onclick=function(e){
e.preventDefault();
var mod=btn.closest(".quattr-module");
if(!mod)return;
var col=btn.closest("[data-col]");
if(!col)return;
var expanded=btn.getAttribute("data-expanded")=="true";
var mpl=parseInt(mod.getAttribute("data-mpl"))||5;
var links=col.querySelectorAll(".q-mcl-link");
links.forEach(function(lnk,i){
if(!(i<mpl))lnk.classList.toggle("q-hidden",expanded);
});
btn.setAttribute("data-expanded",expanded?"false":"true");
btn.textContent=expanded?"View More":"Show Less";
};
});
var rsBtns=container.querySelectorAll(".q-rs-more");
rsBtns.forEach(function(btn){
btn.onclick=function(e){
e.preventDefault();
var mod=btn.closest(".quattr-module");
if(!mod)return;
var expanded=btn.getAttribute("data-expanded")=="true";
var ppr=parseInt(mod.getAttribute("data-ppr"))||5;
var pills=mod.querySelectorAll(".q-rs-pill");
var moreCount=pills.length-ppr;
pills.forEach(function(p,i){
if(i>=ppr)p.classList.toggle("q-hidden",expanded);
});
btn.setAttribute("data-expanded",expanded?"false":"true");
var vmt=mod.getAttribute("data-vmt")||"Show More";
btn.textContent=expanded?vmt+" ("+moreCount+" more)":"\u2190 Show Less";
};
});
var clbBtns=container.querySelectorAll(".q-clb-more");
clbBtns.forEach(function(btn){
btn.onclick=function(e){
e.preventDefault();
var mod=btn.closest(".quattr-module");
if(!mod)return;
var expanded=btn.getAttribute("data-expanded")=="true";
var initVal=parseInt(mod.getAttribute("data-init"))||3;
var boxes=mod.querySelectorAll(".q-clb");
var moreCount=boxes.length-initVal;
boxes.forEach(function(box,i){
if(i>=initVal)box.classList.toggle("q-hidden",expanded);
});
btn.setAttribute("data-expanded",expanded?"false":"true");
btn.textContent=expanded?btn.textContent.split("(")[0].trim()+" ("+moreCount+" more)":"\u2190 Show Less";
};
});
}

function createModuleWrapper(html,showSavePlacement){
var module=document.createElement("div");
module.id="quattr-preview-module";
module.className="quattr-preview-wrapper";
module.style.cssText="position:relative;margin:20px 0;";

var controls=document.createElement("div");
controls.className="quattr-controls";
controls.style.cssText="position:absolute;top:-40px;left:0;right:0;background:#1e3a5f;color:white;padding:8px 12px;display:flex;justify-content:space-between;align-items:center;font-family:system-ui,sans-serif;font-size:12px;border-radius:6px;opacity:0;transition:opacity 0.2s;pointer-events:none;z-index:100;";

var leftControls='<span style="font-weight:600;">Quattr Preview</span>';
var rightControls='<div style="display:flex;gap:4px;">';
rightControls+='<button class="quattr-up" style="background:#3ecf8e;color:white;border:none;padding:4px 10px;border-radius:4px;cursor:pointer;font-size:11px;">↑ Up</button>';
rightControls+='<button class="quattr-down" style="background:#3ecf8e;color:white;border:none;padding:4px 10px;border-radius:4px;cursor:pointer;font-size:11px;">↓ Down</button>';
if(showSavePlacement){
rightControls+='<button class="quattr-save" style="background:#f59e0b;color:white;border:none;padding:4px 10px;border-radius:4px;cursor:pointer;font-size:11px;">&#128190; Save Placement</button>';
}
rightControls+='<button class="quattr-remove" style="background:#ef4444;color:white;border:none;padding:4px 10px;border-radius:4px;cursor:pointer;font-size:11px;">&times; Remove</button>';
rightControls+='</div>';

controls.innerHTML=leftControls+rightControls;

var content=document.createElement("div");
content.className="quattr-content";
content.style.cssText="border:2px dashed transparent;border-radius:8px;transition:border-color 0.2s;background:white;";
content.innerHTML=html;

module.appendChild(controls);
module.appendChild(content);

module.onmouseenter=function(){controls.style.opacity="1";controls.style.pointerEvents="auto";content.style.borderColor="#3ecf8e";};
module.onmouseleave=function(){controls.style.opacity="0";controls.style.pointerEvents="none";content.style.borderColor="transparent";};

return module;
}

function attachModuleEvents(module){
module.querySelector(".quattr-up").onclick=function(e){
e.stopPropagation();
var prev=module.previousElementSibling;
if(prev&&!prev.classList.contains("quattr-preview-wrapper"))prev.insertAdjacentElement("beforebegin",module);
};
module.querySelector(".quattr-down").onclick=function(e){
e.stopPropagation();
var next=module.nextElementSibling;
if(next&&!next.classList.contains("quattr-preview-wrapper"))next.insertAdjacentElement("afterend",module);
};
module.querySelector(".quattr-remove").onclick=function(e){
e.stopPropagation();
module.remove();
};
var saveBtn=module.querySelector(".quattr-save");
if(saveBtn){
saveBtn.onclick=function(e){
e.stopPropagation();
var prev=module.previousElementSibling;
if(prev){
var selector=generateSelector(prev);
var desc=prev.tagName.toLowerCase()+(prev.className?" ."+prev.className.split(" ")[0]:"");
prompt("Copy this selector for your Integration Settings:\\n\\nSelector: "+selector+"\\nPosition: after\\n\\nPaste this in Quattr to save the placement.",selector);
}else{
alert("Move the module below an element first to save its placement.");
}
};
}
}

function generateSelector(el){
if(el.id)return"#"+el.id;
var path=[];
while(el&&el.nodeType==1){
var selector=el.tagName.toLowerCase();
if(el.id){selector="#"+el.id;path.unshift(selector);break;}
else if(el.className){
var classes=el.className.trim().split(/\\s+/).filter(function(c){return c&&!c.startsWith("quattr");}).slice(0,2).join(".");
if(classes)selector+="."+classes;
}
var sib=el,nth=1;
while(sib=sib.previousElementSibling){if(sib.tagName==el.tagName)nth++;}
if(nth>1)selector+=":nth-of-type("+nth+")";
path.unshift(selector);
el=el.parentElement;
if(path.length>4)break;
}
return path.join(" > ");
}

function autoInsertModule(targetEl,html,position){
var module=createModuleWrapper(html,false);
if(position=="before")targetEl.insertAdjacentElement("beforebegin",module);
else if(position=="inside")targetEl.insertAdjacentElement("afterbegin",module);
else if(position=="inside_start")targetEl.insertAdjacentElement("afterbegin",module);
else if(position=="inside_end")targetEl.insertAdjacentElement("beforeend",module);
else targetEl.insertAdjacentElement("afterend",module);
attachModuleEvents(module);
setupViewMoreHandlers(module);
setupCarouselHandlers(module);

var toast=document.createElement("div");
toast.style.cssText="position:fixed;bottom:20px;left:50%;transform:translateX(-50%);background:#1e3a5f;color:white;padding:12px 24px;border-radius:8px;font-family:system-ui,sans-serif;font-size:14px;z-index:100001;box-shadow:0 4px 20px rgba(0,0,0,0.3);";
toast.innerHTML="&#10003; Module inserted at saved placement. Hover to move/remove.";
document.body.appendChild(toast);
setTimeout(function(){toast.remove();},4000);
}

function startPlacement(html,existingSelector){
if(window.__quattrPlacementCleanup){try{window.__quattrPlacementCleanup();}catch(err){}}
var strays=document.querySelectorAll("#quattr-toolbar,#quattr-highlight");
for(var si=0;si<strays.length;si++){strays[si].remove();}

var isPlacing=true;
var highlight=null;
var toolbar=document.createElement("div");
toolbar.id="quattr-toolbar";
toolbar.style.cssText="position:fixed;top:10px;left:50%;transform:translateX(-50%);z-index:100000;background:#1e3a5f;color:white;padding:12px 20px;border-radius:8px;font-family:system-ui,sans-serif;font-size:14px;display:flex;align-items:center;gap:15px;box-shadow:0 4px 20px rgba(0,0,0,0.3);";
toolbar.innerHTML="<span>Click where you want to insert the module</span><button id=quattr-cancel style='background:#ef4444;color:white;border:none;padding:6px 12px;border-radius:4px;cursor:pointer;'>Cancel</button>";
document.body.appendChild(toolbar);
toolbar.querySelector("#quattr-cancel").onclick=cleanup;

highlight=document.createElement("div");
highlight.id="quattr-highlight";
highlight.style.cssText="position:absolute;pointer-events:none;border:3px dashed #3ecf8e;background:rgba(62,207,142,0.1);z-index:99999;transition:all 0.15s ease;display:none;";
document.body.appendChild(highlight);

var lastTarget=null;
function onMove(e){
if(!isPlacing)return;
var el=e.target;
if(el.id=="quattr-toolbar"||el.id=="quattr-highlight"||el.closest("#quattr-toolbar")||el.closest(".quattr-preview-wrapper"))return;
while(el&&el!=document.body&&(el.tagName=="A"||el.tagName=="SPAN"||el.tagName=="IMG"||el.tagName=="BUTTON"||getComputedStyle(el).display=="inline")){el=el.parentElement;}
if(!el||el==document.body||el==document.documentElement)return;
if(el!=lastTarget){
lastTarget=el;
var rect=el.getBoundingClientRect();
highlight.style.display="block";
highlight.style.top=(rect.top+window.scrollY)+"px";
highlight.style.left=(rect.left+window.scrollX)+"px";
highlight.style.width=rect.width+"px";
highlight.style.height=rect.height+"px";
}}

function onClick(e){
if(!isPlacing)return;
if(e.target.id=="quattr-cancel"||e.target.closest("#quattr-toolbar")||e.target.closest(".quattr-preview-wrapper"))return;
e.preventDefault();
e.stopPropagation();
var el=e.target;
while(el&&el!=document.body&&(el.tagName=="A"||el.tagName=="SPAN"||el.tagName=="IMG"||el.tagName=="BUTTON"||getComputedStyle(el).display=="inline")){el=el.parentElement;}
if(!el||el==document.body)return;

var module=createModuleWrapper(html,true);
el.insertAdjacentElement("afterend",module);
attachModuleEvents(module);
setupViewMoreHandlers(module);
setupCarouselHandlers(module);
cleanup();
}

function cleanup(){
isPlacing=false;
document.removeEventListener("mousemove",onMove,true);
document.removeEventListener("click",onClick,true);
if(highlight)highlight.remove();
if(toolbar)toolbar.remove();
if(window.__quattrPlacementCleanup==cleanup)window.__quattrPlacementCleanup=null;
}

window.__quattrPlacementCleanup=cleanup;
document.addEventListener("mousemove",onMove,true);
document.addEventListener("click",onClick,true);
}
})();`;

  return 'javascript:' + encodeURIComponent(code);
}

/**
 * Get cloud service info for display
 */
export function getCloudServiceInfo() {
  return {
    provider: CLOUD_CONFIG.provider,
    isTemporary: CLOUD_CONFIG.provider === 'npoint',
    isLocalDev: CLOUD_CONFIG.provider === 'npoint',
    message:
      CLOUD_CONFIG.provider === 'npoint'
        ? 'Using temporary storage (npoint.io) - local dev mode'
        : 'Using Quattr cloud storage (Redis)',
    bookmarkletApiUrl:
      CLOUD_CONFIG.provider === 'npoint'
        ? `https://api.npoint.io/${CLOUD_CONFIG.documentId}`
        : `${CLOUD_CONFIG.backendApiUrl}${CLOUD_CONFIG.publicEndpoint}`,
  };
}

/**
 * Get the API URL for the bookmarklet based on schema and moduleId
 * For Quattr provider, we need both schema and moduleId to access the public endpoint
 * @param schema - Customer schema (e.g., 'quattr', 'customer_name')
 * @param moduleId - Module ID for the internal linking module
 */
export function getBookmarkletApiUrl(schema?: string, moduleId?: string): string {
  if (CLOUD_CONFIG.provider === 'npoint') {
    return `https://api.npoint.io/${CLOUD_CONFIG.documentId}`;
  }
  // For Quattr, use the public endpoint with schema and moduleId
  // URL: https://api-dev.quattr.com/public-api/template-config/{schema}/{moduleId}
  const cleanSchema = schema?.startsWith('_') ? schema.substring(1) : schema || 'default';
  const safeModuleId = moduleId || 'default';
  return `${CLOUD_CONFIG.backendApiUrl}${CLOUD_CONFIG.publicEndpoint}/${cleanSchema}/${safeModuleId}`;
}
